/**
 * Handler Registry
 */

import type { HandlerEntry } from './core';
import { INGESTION_IDEMPOTENCY_FIELD } from '../../schemas/terra-ingestion';

export const HANDLER_REGISTRY = [
  {
    id: 'none',
    label: 'No backend action',
    description: 'Frontend-only, no server call',
    category: 'none',
    configSchema: [],
  },
  {
    id: 'webhook',
    label: 'Send results to a URL',
    description: 'POST data to an external endpoint when the user submits',
    category: 'webhook',
    summaryTemplate: 'POST → {{callbackUrl}}',
    confirmVerb: 'Send',
    suggestedInteraction: 'confirm' as const,
    requirements: [
      {
        id: 'destination',
        label: 'Set the destination',
        description: 'Where should results be sent when a user takes this action?',
        fieldKeys: ['callbackUrl'],
      },
      {
        id: 'correlation',
        label: 'Choose a correlation field',
        description: 'Optional: identify items so the receiving system can match them',
        fieldKeys: ['referenceKeyField'],
        optional: true,
      },
      {
        id: 'authentication',
        label: 'Authentication',
        description: 'Optional: send an auth header with webhook requests',
        fieldKeys: ['webhookAuthHeader', 'webhookAuth'],
        optional: true,
      },
    ],
    configSchema: [
      {
        key: 'callbackUrl',
        type: 'url',
        label: 'Destination URL',
        userQuestion: 'Where should the results be sent?',
        purpose: 'The endpoint that receives a POST when a user takes this action',
        hint: 'Where to send the results when a user takes this action',
        required: true,
        placeholder: 'https://hooks.clay.com/...',
        layout: { width: 'full' },
      },
      {
        key: 'referenceKeyField',
        type: 'field_ref',
        label: 'Correlation field',
        userQuestion: 'Which field identifies this item?',
        purpose: 'Sent as referenceKey in the webhook payload so the receiving system can match results back',
        hint: 'Defaults to externalId — override to use a different data field',
        required: false,
        defaultValue: INGESTION_IDEMPOTENCY_FIELD,
        layout: { width: 'half' },
      },
      {
        key: 'webhookAuthHeader',
        type: 'text',
        label: 'Auth Header Name',
        userQuestion: 'Which HTTP header should carry the auth value?',
        purpose: 'The HTTP header name for authentication (defaults to Authorization)',
        hint: 'e.g., x-clay-webhook-auth, Authorization, X-API-Key',
        required: false,
        placeholder: 'x-clay-webhook-auth',
      },
      {
        key: 'webhookAuth',
        type: 'text',
        label: 'Auth Value',
        userQuestion: 'What authentication should be sent with webhook requests?',
        purpose: 'The value sent in the auth header (e.g., a shared secret or Bearer token)',
        hint: 'e.g., Bearer sk_live_..., 0da9f1ef149e64a1be60',
        required: false,
      },
    ],
  },
  {
    id: 'salesforce_patch',
    label: 'Update Salesforce record',
    description: 'Update fields on an existing Salesforce record',
    category: 'crm_update',
    wizardTabLabel: 'Salesforce Update',
    mergeInteractionTab: true,
    summaryTemplate: 'PATCH {{objectApiName}}',
    confirmVerb: 'Update Salesforce',
    suggestedInteraction: 'modal' as const,
    requirements: [
      {
        id: 'sf_connection',
        label: 'Connect to Salesforce',
        description: 'Specify the Salesforce object and how to locate the record',
        fieldKeys: ['objectApiName', 'recordIdField'],
      },
    ],
    configSchema: [
      {
        key: 'objectApiName',
        type: 'text',
        label: 'Salesforce Object',
        userQuestion: 'Which Salesforce object are you updating?',
        purpose: 'The API name of the Salesforce object to patch',
        hint: 'e.g., Opportunity, Account, Contact',
        required: true,
        placeholder: 'Opportunity',
        layout: { width: 'half', group: 'connection' },
      },
      {
        key: 'recordIdField',
        type: 'field_ref',
        label: 'Record ID field',
        userQuestion: 'Which data field has the Salesforce record ID?',
        purpose: 'Identifies the specific record to update in Salesforce',
        hint: 'Which data field has the Salesforce record ID?',
        required: true,
        layout: { width: 'half', group: 'connection' },
      },
    ],
  },
  {
    /**
     * Approve a generated draft message and push it toward the Gong flow.
     *
     * Registered HERE as well as in services/execution-handlers — this registry is
     * what `ActionBindingSchema` builds its handler enum from, so a blueprint
     * binding naming a handler that is absent here fails validation and
     * blueprint-cache drops the WHOLE blueprint as invalid. (Found the hard way:
     * seeding the binding first made every signal blueprint unloadable.)
     *
     * No configSchema: the handler reads the draft from the artifact it is acting
     * on, so there is nothing for an admin to configure.
     */
    id: 'approve_message',
    label: 'Edit & approve generated message',
    description: 'Approve the draft on this artifact and queue it for the Gong flow',
    category: 'notification',
    summaryTemplate: 'Approve draft → Gong flow',
    confirmVerb: 'Approve',
    // Sending to a real person is worth one deliberate look at the copy first.
    suggestedInteraction: 'review' as const,
    configSchema: [],
  },
  {
    /**
     * Generic "run a named backend service" handler.
     *
     * `skill` is the edit-with-AI agent loop over an existing artifact and
     * `none` + a bespoke route would bypass action executions/outcomes — so a
     * blueprint action that needs backend generation logic (first user: Deal
     * Brief `regenerate` → `deal_brief.regenerate`) binds `internal` and names
     * the service. The runtime side is `services/execution-handlers/internal.ts`,
     * which looks `service` up in `internal-services.ts`; features register
     * there at module load via `internal-services.registry.ts`.
     */
    id: 'internal',
    label: 'Internal service',
    description: 'Runs a named backend service against the artifact — no external call. Config: { service }',
    category: 'internal',
    summaryTemplate: 'Run {{service}}',
    confirmVerb: 'Run',
    suggestedInteraction: 'confirm' as const,
    requirements: [
      {
        id: 'service',
        label: 'Choose the service',
        description: 'Which registered backend service runs when a user takes this action?',
        fieldKeys: ['service'],
      },
    ],
    configSchema: [
      {
        key: 'service',
        type: 'text',
        label: 'Service',
        userQuestion: 'Which backend service should run?',
        purpose: 'A registered internal service name (namespace.action) — see internal-services.registry.ts',
        hint: 'e.g., deal_brief.regenerate',
        required: true,
        placeholder: 'deal_brief.regenerate',
        layout: { width: 'full' },
      },
    ],
  },
] as const satisfies readonly HandlerEntry[];

export type HandlerType = (typeof HANDLER_REGISTRY)[number]['id'];

export function getHandlerEntry(id: HandlerType | (string & {})): HandlerEntry | undefined {
  return (HANDLER_REGISTRY as readonly HandlerEntry[]).find((e) => e.id === id);
}
